import z from 'zod';

export const getError = (path, formErrors) => {
  let current = formErrors;
  for (const key of path) {
    if (!current) return undefined;
    current = current[key];
  }
  return current;
};
const clearErrorByPath = (path, setFormErrors) => {
  setFormErrors((prev) => {
    const next = structuredClone(prev);
    const removeNested = (obj, keys) => {
      const last = keys.pop();
      const parent = keys.reduce((acc, k) => acc?.[k], obj);
      if (parent && last in parent) delete parent[last];
    };
    removeNested(next, [...path]);
    return next;
  });
};
export const handleChange = (
  path,
  value,
  setState,
  setFormErrors,
  formErrors,
) => {
  if (getError(path, formErrors)) clearErrorByPath(path, setFormErrors);
  setState((prev) => {
    const updated = structuredClone(prev);
    let cur = updated;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const nextKey = path[i + 1];
      if (cur[key] === undefined || typeof cur[key] !== 'object') {
        cur[key] = typeof nextKey === 'number' ? [] : {};
      }
      cur = cur[key];
    }
    cur[path[path.length - 1]] = value;
    return updated;
  });
};

export function emptyObjectToUndefined(schema) {
  return z.preprocess((val) => {
    if (
      val &&
      typeof val === 'object' &&
      Object.values(val).every(
        (v) =>
          v === '' ||
          v === null ||
          v === undefined ||
          (typeof v === 'string' && v.trim() === ''),
      )
    ) {
      return undefined;
    }
    return val;
  }, schema);
}

export const redirectDynamicVariables = ['[%rid%]'];
export const containsDynamicVariable = (url) =>
  redirectDynamicVariables.some((variable) => url.includes(variable));

export function isValidObjectId(id) {
  return /^[a-f\d]{24}$/i.test(id);
}

export const validateURLStructure = (url) => {
  const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/;
  if (!urlPattern.test(url.trim())) {
    return "URL must start with 'http://' or 'https://' and be a valid URL.";
  }

  const [baseUrl, queryParams] = url.split('?');

  const requiredVariablesPattern = /\[\%(rid)\%\]/;
  const disallowedVariablesPattern = /\[\%(?!rid\%\])[a-zA-Z_]+\%\]/;

  const hasRequiredInBase = requiredVariablesPattern.test(baseUrl);
  const hasRequiredInQuery =
    !!queryParams && requiredVariablesPattern.test(queryParams);

  if (!hasRequiredInBase && !hasRequiredInQuery) {
    return 'URL must contain at least one required variable: [%rid%].';
  }

  if (queryParams) {
    if (url.split('?').length > 2) {
      return "URL must contain exactly one '?' to start the query parameters.";
    }

    const queryPattern = /^[^&\s?]+=[^&\s?]*(?:&[^&\s?]+=[^&\s?]*)*$/;
    if (!queryPattern.test(queryParams)) {
      return "Query parameters should be formatted correctly, e.g., 'param1=value1&param2=value2'.";
    }

    if (disallowedVariablesPattern.test(queryParams)) {
      return 'Query parameters contain disallowed variables. Only [%rid%], [%sid%], and [%pid%] are allowed.';
    }

    if (!hasRequiredInBase && !hasRequiredInQuery) {
      return 'Query parameters must contain at least one of the required variables: [%rid%], [%sid%], or [%pid%].';
    }
  }

  return null;
};
const hasValue = (value) =>
  typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
export const isSupplierBankAccountComplete = (bankAccount, options = {}) => {
  if (!bankAccount) return false;

  const requiredFields = [
    bankAccount.accountHolderName,
    bankAccount.bankName,
    bankAccount.accountNumber,
    bankAccount.bankType,
    bankAccount.bankAddress?.street1,
    bankAccount.bankAddress?.country?.value,
    bankAccount.bankAddress?.state?.value,
    bankAccount.bankAddress?.city,
    bankAccount.bankAddress?.postalCode,
  ];

  if (options.requireAccountId) {
    requiredFields.unshift(bankAccount._id);
  }

  return requiredFields.every(hasValue);
};
